import PropTypes from 'prop-types';
import { PureComponent } from 'react';
import cn from 'classnames';

import BrIcons from '@components/BrIcons';
import BrText from '@components/BrText';
import ColorStyles from '@components/ColorStyles';
import { JsUtils } from '@components/UtilComponents/UtilFunctions';

import './brQueryBox.scss';
import BrLoader from '@components/BrLoader';

/* eslint-disable react/no-array-index-key */
export default class BrQueryBox extends PureComponent {
  static propTypes = {
    className: PropTypes.string,
    singleQuery: PropTypes.bool, // if only single query support is needed. By default its multiquery
    query: PropTypes.string, // query string
    placeholder: PropTypes.string,
    queryList: PropTypes.array.isRequired,
    queryFormatter: PropTypes.func.isRequired, // this formats the queryList object into printable string
    selectedQueryComparator: PropTypes.func,
    isQueryAddable: PropTypes.bool,
    isQueryRemovable: PropTypes.bool,
    isQuerySelectable: PropTypes.bool,

    // full list of objects to search from
    suggestionList: PropTypes.array,

    /**
     * A render function that returns a react component that renders the suggested item, with the given query
     *
     * (item, query) => react component
     */
    suggestionFormatter: PropTypes.func,

    /**
     * A comparison function for search, pass the value and the suggestionObj for comparison.
     *
     * (value, suggestionObj) => return boolean
     */
    suggestionComparator: PropTypes.func,

    /**
     * A sorter function to is used to rank search results
     *
     * (value, suggestionObj1, suggestionObj2) => return a number (just as what the compareFunction of Array.sort()
     * expects)
     */
    suggestionSorter: PropTypes.func,

    // If fetchSuggestion function is provided, suggestion list will be generated by the result
    // of the function call
    fetchSuggestion: PropTypes.func,

    onFetchSuggestionFailure: PropTypes.func,

    /**
     * A handler function fired when a new query term is selected/submitted, either the query object or simple string
     * will be passed.
     *
     * query => { statements }
     *
     * If preSubmitDecorator is defined, then
     * preSubmitDecorator(query) => { statements }
     */
    onSubmit: PropTypes.func.isRequired,

    /**
     * A transformation function executed when submitting from text input, to allow passing same query object as
     * using suggestion list selector.
     *
     * query => { return transformed object }
     */
    preSubmitDecorator: PropTypes.func,

    /**
     * A handler function fired when user removes an existing query, query object will be passed.
     */
    onRemoveQuery: PropTypes.func,

    /**
     * A handler function fired when user selects an existing query, query object will be passed.
     */
    onSelectQuery: PropTypes.func,

    maxSuggestionListLength: PropTypes.number,

    disableBlur: PropTypes.bool, // If you want to disable blur behaviour
    showSearchIcon: PropTypes.bool,

    /**
     * Enable this if you want to allow user to paste queries from a file with a delimiter
     */
    allowBreakOnPaste: PropTypes.bool,

    /**
     * Delimiter to be used to break the queries in case of a paste operation
     */
    breakDelimiter: PropTypes.string,
    /**
     * If you want to debounce your fetch suggestions call
     */
    allowDebounce: PropTypes.bool,
    /**
     * Delay in debounce
     */
    debounceDelay: PropTypes.number,
  };

  static defaultProps = {
    singleQuery: false,
    suggestions: [],
    isQueryAddable: true,
    isQueryRemovable: true,
    isQuerySelectable: false,
    maxSuggestionListLength: 10,
    disableBlur: false,
    allowBreakOnPaste: false,
    breakDelimiter: '\n',
  };

  constructor(props) {
    super(props);
    this.state = {
      matchedSuggestions: [],
      removeOnSecondBackspace: false,
      query: this.props.query || '',
      suggestionsLoading: false,
    }; // may use initial props
  }

  // eslint-disable-next-line camelcase
  UNSAFE_componentWillReceiveProps(nextProps) {
    if (nextProps.query !== undefined && this.state.query !== nextProps.query) {
      this.setState({ query: nextProps.query });
    }
    if (nextProps.suggestionList !== this.props.suggestionList) {
      this.setState({ matchedSuggestions: [] });
    }
  }

  handleSelectQuery = (e, query) => {
    if (e) {
      e.stopPropagation();
    }

    const { onSelectQuery } = this.props;
    if (onSelectQuery) {
      onSelectQuery(query);
    }
  };

  handleRemoveQuery = (e, query) => {
    if (e) {
      e.stopPropagation();
    }

    const { onRemoveQuery } = this.props;
    if (onRemoveQuery) {
      onRemoveQuery(query);
    }
  };

  handleKeyUp = (e) => {
    const { queryList } = this.props;
    const { removeOnSecondBackspace } = this.state;
    if (e.key === 'Enter' && e.target.value) {
      this.handleSubmit(e.target.value);
    } else if (e.key === 'Backspace' && !e.target.value) {
      if (queryList.length && removeOnSecondBackspace) {
        this.handleRemoveQuery(null, queryList[queryList.length - 1]);
      } else {
        this.setState({ removeOnSecondBackspace: true });
      }
      return;
    }
    this.setState({ removeOnSecondBackspace: false });
  };

  handleBlur = (e) => {
    if (!this.props.disableBlur) {
      this.handleSubmit(e.target.value);
    }
  };

  getMatchedSuggestions = (value, suggestionList, suggestionComparator, suggestionSorter, maxSuggestionListLength) => {
    const matched = suggestionList.filter((suggestion) => suggestionComparator(value, suggestion));
    if (suggestionSorter) {
      matched.sort((suggestion1, suggestion2) => suggestionSorter(value, suggestion1, suggestion2));
    }
    return matched.slice(0, maxSuggestionListLength);
  };

  fetchFlow = () => {
    const {
      fetchSuggestion,
      onFetchSuggestionFailure,
    } = this.props;
    if (this.state.query) {
      this.setState({ suggestionsLoading: true });
      fetchSuggestion(this.state.query)
        .then((fetchedSuggestions) => {
          this.setState({ suggestionsLoading: false });
          if (this.state.query) {
            this.setState({
              matchedSuggestions: fetchedSuggestions,
            });
          }
        })
        .catch(() => {
          this.setState({ suggestionsLoading: false });
          onFetchSuggestionFailure();
        });
    }
  };

  debouncedCall = JsUtils.debounce(this.fetchFlow, this.props.debounceDelay || 500);

  debouncedSuggestions = JsUtils.debounce(() => {
    if (this.state.query) {
      this.setState({
        matchedSuggestions: this.getMatchedSuggestions(
          this.state.query,
          this.props.suggestionList,
          this.props.suggestionComparator,
          this.props.suggestionSorter,
          this.props.maxSuggestionListLength,
        ),
      });
    }
  }, 500)

  handleInputChange = (e) => {
    // update matchedSuggestions
    const { value } = e.target;
    const {
      fetchSuggestion,
      suggestionComparator,
      allowDebounce,
    } = this.props;
    this.setState({ query: value });
    if (value) {
      if (fetchSuggestion) {
        if (allowDebounce) {
          this.debouncedCall();
        } else {
          this.fetchFlow();
        }
      } else if (suggestionComparator) {
        this.debouncedSuggestions();
      } else {
        this.setState({ matchedSuggestions: [] });
        this.setState({ suggestionsLoading: false });
      }
    } else {
      this.setState({ suggestionsLoading: false });
      this.setState({ matchedSuggestions: [] });
    }
  };

  handleSelectSuggestion = (item) => {
    this.handleSubmit(item);
  };

  handleSubmit = (query) => {
    const { preSubmitDecorator, onSubmit } = this.props;
    if (typeof query === 'string') {
      if (!query.trim()) {
        return;
      }
      if (preSubmitDecorator) {
        onSubmit(preSubmitDecorator(query, this.state.matchedSuggestions));
      } else {
        onSubmit(query);
      }
    } else {
      onSubmit(query);
    }
    this.setState({
      matchedSuggestions: [],
      removeOnSecondBackspace: false,
      query: '',
    });
  };

  handleCompareSelected = (query) => {
    const { selectedQueryComparator } = this.props;
    return selectedQueryComparator && selectedQueryComparator(query);
  };

  handlePaste = (event) => {
    const { allowBreakOnPaste, breakDelimiter } = this.props;
    if (allowBreakOnPaste) {
      event.preventDefault();
      const pastedText = (event.clipboardData || window.clipboardData).getData('text');
      const queries = pastedText.split(breakDelimiter);
      queries.forEach((query) => {
        this.handleSubmit(query);
      });
    }
  };

  render() {
    const {
      className,
      singleQuery,
      placeholder,
      queryList,
      queryFormatter,
      suggestionFormatter,
      isQueryAddable,
      isQueryRemovable,
      isQuerySelectable,
      showSearchIcon,
    } = this.props;
    const { matchedSuggestions, query, suggestionsLoading } = this.state;
    const classname = cn('br-query-box', className);
    return (
      <div className={classname} tabIndex="0" data-id="br-query-box">
        <div className="query-area" onClick={() => this.inputBox.focus()}>
          {!singleQuery && (
            <div className={`query-list${isQuerySelectable ? ' selectable' : ''}`}>
              {queryList.map((query, idx) => {
                const selected = this.handleCompareSelected(query) ? 'selected' : '';
                return (
                  <div key={idx} className={`query-term ${selected}`} onClick={(e) => this.handleSelectQuery(e, query)}>
                    <BrText>{queryFormatter(query)}</BrText>
                    {isQueryRemovable && (
                      <span className="remove-query" onClick={(e) => this.handleRemoveQuery(e, query)}>
                        <BrIcons.Cross
                          isSubComp
                          colorStyle={selected || !isQuerySelectable ? ColorStyles.GRAY : ColorStyles.BLUE}
                        />
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          )}
          {(singleQuery || isQueryAddable || queryList.length === 0) && (
            <input
              autoFocus
              placeholder={placeholder}
              value={this.state.query}
              ref={(ele) => {
                this.inputBox = ele;
              }}
              className="query-input"
              type="text"
              tabIndex="-1"
              onBlur={this.handleBlur}
              onChange={this.handleInputChange}
              onKeyUp={this.handleKeyUp}
              onPaste={this.handlePaste}
            />
          )}
          {showSearchIcon && <BrIcons.Search />}
        </div>
        {
          suggestionsLoading
            ? (
              <div className="suggestion-list loading-item">
                <BrLoader />
              </div>
            )
            : (
              !!matchedSuggestions.length && (
                <div className="suggestion-list">
                  {matchedSuggestions.map((item, idx) => (
                    <div key={idx} className="suggestion-item" onMouseDown={() => this.handleSelectSuggestion(item)}>
                      <BrText>{suggestionFormatter(item, query)}</BrText>
                    </div>
                  ))}
                </div>
              )
            )
        }
      </div>
    );
  }
}
